import { BreakpointObserver } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Injector,
  OnInit,
  runInInjectionContext,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { AmlDeskErrorHandlerService } from '@core/services/aml-desk-error-handler.service';
import { CookieConsentService } from '@core/services/cookie-consent.service';
import {
  ErrorSeverityMap,
  LoaderComponent,
  NAVIGATION_BREAKPOINTS,
  NavigationFacade,
} from '@kerberos-compliance/compliance-shared-ui';
import { combineLatest } from 'rxjs';
import { filter, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { AuthStore } from '@/auth/store/auth.store';
import { UserProfileFacade } from '@/user/facades/user-profile.facade';
import { AppStore } from './store/app.store';

@Component({
  selector: 'ad-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.less',
  imports: [RouterOutlet, LoaderComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  private readonly breakpointObserver = inject(BreakpointObserver);

  private readonly appStore = inject(AppStore);

  private readonly cookieConsentService = inject(CookieConsentService);

  private readonly userProfileFacade = inject(UserProfileFacade);

  private readonly errorHandlerService = inject(AmlDeskErrorHandlerService);

  private readonly destroyRef = inject(DestroyRef);

  private readonly authStore = inject(AuthStore);

  private readonly injector = inject(Injector);

  private readonly navigationFacade = inject(NavigationFacade);

  public readonly loading = this.appStore.globalLoading;

  public constructor() {
    this.setupBreakpoints();
  }

  public ngOnInit(): void {
    void this.initializeApplicationWithErrorHandling();
  }

  /**
   * Initializes the session-scoped services once the user is logged in and their profile has loaded:
   * currently only cookie consent. Document prefetching used to live here too, but the document library
   * fetches its own data on demand since the CenterDevice caches were dropped (KCP-1193).
   *
   * @returns {void} This method does not return any value.
   */
  private initializeServicesIfUserIsLoggedIn(): void {
    const isUserLoggedIn$ = runInInjectionContext(this.injector, () =>
      toObservable(this.authStore.isUserLoggedIn).pipe(shareReplay({ bufferSize: 1, refCount: true })),
    );

    const userProfileLoadCompleted$ = runInInjectionContext(this.injector, () =>
      toObservable(this.userProfileFacade.userProfileLoadCompleted),
    );

    const whenUserProfileReady$ = combineLatest([isUserLoggedIn$, userProfileLoadCompleted$]).pipe(
      filter(([isLoggedIn, profileLoaded]) => isLoggedIn && profileLoaded),
      take(1),
    );

    const cookieConsent$ = whenUserProfileReady$.pipe(
      switchMap(() =>
        this.cookieConsentService.initialize().pipe(
          tap(() => {
            const userProfile = this.userProfileFacade.userProfile();
            if (userProfile?.userPreferences?.cookieConsent) {
              this.cookieConsentService.applySavedConsent(userProfile.userPreferences.cookieConsent);
            }
          }),
          switchMap(() => this.cookieConsentService.showConsentBannerIfNeeded()),
        ),
      ),
    );

    cookieConsent$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      error: (error: unknown) => this.reportPrefetchFailure(error, 'Initializing session-scoped services'),
    });
  }

  /**
   * Configures and sets up breakpoints for observing changes in screen size
   * and dispatching corresponding actions based on the observed breakpoint.
   *
   * @returns {void} This method does not return a value.
   */
  private setupBreakpoints(): void {
    for (const breakPoint of NAVIGATION_BREAKPOINTS) {
      this.breakpointObserver
        .observe([breakPoint.mediaQuery])
        .pipe(
          filter((result) => result.matches),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => {
          this.navigationFacade.setScreen(breakPoint.screen);
          if (breakPoint.openNavigation) {
            this.navigationFacade.open();
          } else {
            this.navigationFacade.close();
          }
        });
    }
  }

  /**
   * Reports a failed background initialization. Nothing on screen waits for these, so a failure is logged
   * and left there — but it must be *handled*: an rxjs subscription without an error callback escalates
   * the failure to an unhandled window error (AML-DESK-AW).
   *
   * @param {unknown} error - The error the initialization failed with.
   * @param {string} action - The step being attempted, for the error metadata.
   * @returns {void} This method does not return a value.
   */
  private reportPrefetchFailure(error: unknown, action: string): void {
    this.errorHandlerService.handleError({
      error,
      metadata: {
        severity: ErrorSeverityMap.Low,
        component: 'AppComponent',
        action,
      },
      silent: true,
    });
  }

  /**
   * Hides the browser's address bar on mobile devices by causing a slight scroll after a delay.
   *
   * This method checks if the page is scrolled to the very top and, if so, performs a small scroll
   * to trigger the hiding of the address bar. The action is delayed to allow initial rendering to complete.
   *
   * @returns {void} Does not return a value.
   */
  private quickHideAddressBar(): void {
    setTimeout(() => {
      if (window.scrollY !== 0) {
        return;
      }
      window.scrollTo(0, window.scrollY + 1);
    }, 1000);
  }

  /**
   * Initializes the application and incorporates error handling to manage any errors that occur during the process.
   * If an error arises, it delegates the error details and context metadata to the error handling service.
   *
   * Never rejects: a failed bootstrap is reported and swallowed so the app can still
   * render a fallback UI, which is why `ngOnInit` may discard the returned promise.
   *
   * @returns {Promise<void>} Resolves once initialization has finished or its failure has been reported.
   */
  private async initializeApplicationWithErrorHandling(): Promise<void> {
    try {
      await this.initializeApplication();
    } catch (error) {
      this.errorHandlerService.handleError({
        error,
        metadata: {
          severity: ErrorSeverityMap.High,
          component: 'AppComponent',
          action: 'Failed to initialize application',
        },
        // The fallback UI is the feedback
        silent: true,
      });
    }
  }

  /**
   * Initializes the application by performing essential setup processes
   * and starting the session-scoped services.
   *
   * @returns {Promise<void>} A promise that resolves when the application initialization is complete.
   */
  private async initializeApplication(): Promise<void> {
    this.quickHideAddressBar();
    this.initializeServicesIfUserIsLoggedIn();
  }
}
